'use client';

import { Stack, Heading, Icon, Box } from '@chakra-ui/react';
import { Link } from '@chakra-ui/next-js';
import { ROUTES } from '@habity/constants';
import { ICONS } from '~/constants/icons';
import { useUser } from '~/hooks/useUser';
import { PropsWithChildren } from 'react';
import { useUIStore } from '~/store';
import { getSellPath } from '@habity/services';
import { FooterContent } from '../footer/Content';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: (...args: any) => void;
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const { user } = useUser();
  const openLoginModal = useUIStore((state) => state.openLoginModal);

  return (
    <Stack
      borderWidth="0"
      position="fixed"
      w="100vw"
      h="100vh"
      overflowY="hidden"
      // Put far below the screen due to in-app browsers mispositioning it
      top="100vh"
      backgroundColor="black"
      color="white"
      flex="1"
      justifyContent="flex-start"
      gap="20"
      zIndex="overlay"
      transform={isOpen ? 'translateY(-100%)' : 'translateY(100%)'}
      transition="transform 0.2s ease-in-out"
    >
      {/* Close button */}
      <Box position="absolute" top="4" right="4" onClick={onClose}>
        <Icon as={ICONS.CLOSE} color="white" boxSize="28px" />
      </Box>

      {/* Content */}
      <Stack overflowY="scroll" pt="40" spacing="20">
        {/* Main */}
        <Stack gap="4" px="10">
          {!user && (
            <FadeIn show={isOpen} delay={0.1}>
              <Heading
                color="white"
                onClick={async () => {
                  onClose();
                  openLoginModal();
                }}
              >
                Logga in
              </Heading>
            </FadeIn>
          )}

          <FadeIn show={isOpen} delay={0.2}>
            <Link href={ROUTES.SEARCH.HOMES}>
              <Heading color="white" onClick={onClose}>
                Våra bostäder
              </Heading>
            </Link>
          </FadeIn>

          <FadeIn show={isOpen} delay={0.3}>
            <Link href={ROUTES.SEARCH.BROKERS}>
              <Heading color="white" onClick={onClose}>
                Våra mäklare
              </Heading>
            </Link>
          </FadeIn>

          <FadeIn show={isOpen} delay={0.4}>
            <Link href={ROUTES.SEARCH.MUNICIPALITIES}>
              <Heading color="white" onClick={onClose}>
                Våra områden
              </Heading>
            </Link>
          </FadeIn>

          <FadeIn show={isOpen} delay={0.5}>
            <Link href={ROUTES.CMS.HOW_IT_WORKS}>
              <Heading color="white" onClick={onClose}>
                Vår tjänst
              </Heading>
            </Link>
          </FadeIn>

          <FadeIn show={isOpen} delay={0.6}>
            <Link href={getSellPath(undefined, 'estimate')}>
              <Heading color="white" onClick={onClose}>
                Värdera
              </Heading>
            </Link>
          </FadeIn>
        </Stack>

        <FadeIn show={isOpen} delay={0.7}>
          <Stack px="10" pb="40" onClick={onClose}>
            <FooterContent />
          </Stack>
        </FadeIn>
      </Stack>
    </Stack>
  );
}

interface FadeInProps extends PropsWithChildren {
  show: boolean;
  delay: number;
}

function FadeIn({ show, delay = 0, children }: FadeInProps) {
  return (
    <Box
      opacity={show ? 1 : 0}
      transform={show ? 'translateY(0px)' : 'translateY(10px)'}
      transition="all 0.6s ease"
      transitionDelay={show ? `${delay}s` : '0.1s'} // Slower IN, faster OUT
    >
      {children}
    </Box>
  );
}
